import ds from 'views/styles/designSystem'
import { pxTo } from 'design-system-utils'
import { createComponent } from 'react-fela'
import PropTypes from 'prop-types'
import { getContrastTextColor } from 'utils/colors'

const documentBaseFontSize = ds.get('type.baseFontSize')
const xsTextSize = ds.get('type.sizes.buttons.xs')
const defaultTextSize = ds.get('type.sizes.buttons.default')

const textSizes = {
  xs: pxTo(xsTextSize, documentBaseFontSize, 'rem'),
  xxs: pxTo(xsTextSize, documentBaseFontSize, 'rem'),
  sm: pxTo(defaultTextSize, documentBaseFontSize, 'rem'),
  md: pxTo(defaultTextSize, documentBaseFontSize, 'rem'),
  lg: pxTo(defaultTextSize, documentBaseFontSize, 'rem'),
}

const defaultSpacing = pxTo(2, defaultTextSize, 'em')
const spacing = {
  xxs: pxTo(1.7, xsTextSize, 'em'),
  xs: pxTo(1.7, xsTextSize, 'em'),
  sm: defaultSpacing,
  md: defaultSpacing,
  lg: defaultSpacing,
}

const height = {
  xxs: pxTo(20, xsTextSize, 'em'),
  xs: pxTo(30, xsTextSize, 'em'),
  sm: pxTo(40, defaultTextSize, 'em'),
  md: pxTo(50, defaultTextSize, 'em'),
  lg: pxTo(60, defaultTextSize, 'em'),
}

const defaultPadding = `${pxTo(10, defaultTextSize, 'em')} ${pxTo(25, defaultTextSize, 'em')}`
const padding = {
  xxs: `0 ${pxTo(10, xsTextSize, 'em')}`,
  xs: `0 ${pxTo(20, xsTextSize, 'em')}`,
  sm: defaultPadding,
  md: defaultPadding,
  lg: defaultPadding,
}

const background = {
  primary: ds.brand('gradient'),
  secondary: ds.brand('magneticBlack'),
  tertiary: ds.brand('greyLight'),
  quaternary: ds.brand('skyBlue'),
  reversed: ds.brand('white'),
  outline: 'transparent',
}

const color = {
  primary: ds.brand('white'),
  secondary: ds.brand('white'),
  tertiary: ds.brand('magneticBlack'),
  quaternary: ds.brand('white'),
  outline: ds.brand('white'),
}

const getBackgroundColor = ({ customColor, brand, reversed, outline }) => {
  return (
    customColor ||
    (reversed === true && brand !== 'primary'
      ? background['reversed']
      : reversed === true && brand === 'primary'
      ? background['primary']
      : outline === true
      ? background['outline']
      : background[brand])
  )
}

const getColor = ({ customColor, brand, reversed, outline }) => {
  return customColor
    ? getContrastTextColor(customColor)
    : outline === true
    ? color['outline']
    : reversed === true
    ? background[brand]
    : color[brand]
}
const Button = createComponent(
  ({ size = 'sm', brand, block, reversed, outline, nowrap, customColor }) => ({
    textAlign: 'center',
    padding: padding[size],
    width: block ? '100%' : 'auto',
    minHeight: height[size],
    fontSize: textSizes[size],
    textTransform: 'uppercase',
    fontWeight: ds.get('type.fontWeight.bold'),
    letterSpacing: spacing[size],
    whiteSpace: nowrap ? 'nowrap' : 'inherit',
    background: getBackgroundColor({
      customColor,
      outline,
      reversed,
      brand,
    }),
    color: getColor({
      customColor,
      outline,
      reversed,
      brand,
    }),
    border:
      outline === true
        ? `${pxTo(ds.get('border.width.inputs'), documentBaseFontSize, 'rem')} currentColor ${ds.get('border.style')}`
        : 0,
    borderRadius: pxTo(ds.get('border.radius.buttons'), ds.get('type.baseFontSize'), 'rem'),
    cursor: 'pointer',
    position: 'relative',
    '-webkit-text-fill-color': reversed === true && brand === 'primary' ? 'transparent' : 'initial',
    '-webkit-background-clip': reversed === true && brand === 'primary' ? 'text' : 'initial',
    ':after': {
      position: 'absolute',
      top: '0px',
      left: '0px',
      width: '100%',
      height: '100%',
      zIndex: -1,
      pointerEvents: 'none',
      content: '" "',
      borderRadius: pxTo(ds.get('border.radius.buttons'), ds.get('type.baseFontSize'), 'rem'),
    },
    ':hover': {
      color: outline === true || reversed === true ? background[brand] : color[brand],
      background:
        reversed === true && brand !== 'primary'
          ? background['reversed']
          : reversed === true && brand === 'primary'
          ? background['primary']
          : outline === true && brand !== 'primary'
          ? background['reversed']
          : outline === true && brand === 'primary'
          ? background['primary']
          : background[brand],
      '-webkit-text-fill-color':
        (reversed === true && brand === 'primary') || (outline === true && brand === 'primary')
          ? 'transparent'
          : 'initial',
      '-webkit-background-clip':
        (reversed === true && brand === 'primary') || (outline === true && brand === 'primary') ? 'text' : 'initial',
      border:
        outline === true
          ? `${pxTo(ds.get('border.width.inputs'), documentBaseFontSize, 'rem')} currentColor ${ds.get('border.style')}`
          : 0,
      ':after': {
        zIndex: 2,
        backgroundColor: outline === true ? 'transparent' : 'rgba(0,0,0,0.1)',
      },
    },
    ':focus': {
      color: outline === true || reversed === true ? background[brand] : color[brand],
      background:
        reversed === true && brand !== 'primary'
          ? background['reversed']
          : reversed === true && brand === 'primary'
          ? background['primary']
          : outline === true && brand !== 'primary'
          ? background['reversed']
          : outline === true && brand === 'primary'
          ? background['primary']
          : background[brand],
      '-webkit-text-fill-color':
        (reversed === true && brand === 'primary') || (outline === true && brand === 'primary')
          ? 'transparent'
          : 'initial',
      '-webkit-background-clip':
        (reversed === true && brand === 'primary') || (outline === true && brand === 'primary') ? 'text' : 'initial',
      border: 0,
      ':after': {
        zIndex: 2,
        backgroundColor: outline === true ? 'transparent' : 'rgba(0,0,0,0.1)',
      },
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      ':hover': {
        ':after': {
          zIndex: 2,
          backgroundColor: 'rgba(255,255,255,0.4)',
        },
      },
      ':focus': {
        ':after': {
          zIndex: 2,
          backgroundColor: 'rgba(255,255,255,0.4)',
        },
      },
      ':after': {
        zIndex: 2,
        backgroundColor: 'rgba(255,255,255,0.4)',
      },
    },
  }),
  'button',
  ['onClick', 'size', 'brand', 'disabled', 'reversed', 'type', 'data-cy'],
)

Button.defaultProps = {
  block: false,
  outline: false,
  reversed: false,
}

Button.propTypes = {
  onClick: PropTypes.func,
  outline: PropTypes.bool,
  reversed: PropTypes.bool,
  size: PropTypes.oneOf(['xxs', 'xs', 'sm', 'md', 'lg']),
  brand: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'quaternary']),
  customColor: PropTypes.string,
  nowrap: PropTypes.string,
}

export default Button
